import { Controller } from "@hotwired/stimulus"

/**
 * Mobile navigation drawer (DaisyUI checkbox drawer, below lg).
 *
 * The checkbox drives the CSS; this controller adds what the checkbox can't:
 * - the hamburger (a <label>) works from the keyboard and reports aria-expanded
 * - Escape closes the drawer, focus moves into it on open and back to the hamburger on close
 * - links close it before Turbo navigates (`click->drawer#close`)
 * - `turbo:before-cache` closes it, otherwise Back/Forward restores the page with it open
 */
export default class extends Controller<HTMLElement> {
  static targets = ["checkbox", "toggle", "panel", "closeButton"]

  declare readonly checkboxTarget: HTMLInputElement
  declare readonly toggleTarget: HTMLElement
  declare readonly panelTarget: HTMLElement
  declare readonly closeButtonTarget: HTMLElement
  declare readonly hasToggleTarget: boolean
  declare readonly hasCloseButtonTarget: boolean

  private readonly handleKeydown = (event: KeyboardEvent) => {
    if (event.key === "Escape" && this.checkboxTarget.checked) this.close()
  }

  private readonly handleBeforeCache = () => this.setOpen(false)

  connect() {
    document.addEventListener("keydown", this.handleKeydown)
    document.addEventListener("turbo:before-cache", this.handleBeforeCache)
    this.setOpen(false)
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown)
    document.removeEventListener("turbo:before-cache", this.handleBeforeCache)
  }

  // Checkbox changed by a label click (hamburger, close button, overlay)
  sync() {
    const open = this.checkboxTarget.checked
    this.updateToggle(open)

    if (open) {
      if (this.hasCloseButtonTarget) this.closeButtonTarget.focus({ preventScroll: true })
    } else if (this.panelTarget.contains(document.activeElement)) {
      this.focusToggle()
    }
  }

  // Enter/Space on the hamburger label (labels are not keyboard-activatable)
  toggleKey(event: KeyboardEvent) {
    if (event.key !== "Enter" && event.key !== " ") return

    event.preventDefault()
    this.checkboxTarget.click()
  }

  close() {
    if (!this.checkboxTarget.checked) return

    const focusWasInside = this.panelTarget.contains(document.activeElement)
    this.setOpen(false)
    if (focusWasInside) this.focusToggle()
  }

  private setOpen(open: boolean) {
    this.checkboxTarget.checked = open
    this.updateToggle(open)
  }

  private updateToggle(open: boolean) {
    if (this.hasToggleTarget) this.toggleTarget.setAttribute("aria-expanded", String(open))
  }

  private focusToggle() {
    if (this.hasToggleTarget) this.toggleTarget.focus({ preventScroll: true })
  }
}
